import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../services/api.js';
import { chargerQrProblem } from '../../config/index.js';
import { usePublicConfig } from '../../config/demo.js';
import QrScanner from '../../components/QrScanner.jsx';
import './guest-checkout-session.scss';

const BLUE = { color: '#005293' };
const STORE_KEY = 'chargeos.guestSession';
const METHODS = [
  { id: 'gcash', label: 'GCash', icon: 'fa-mobile-screen-button', tone: 'text-primary' },
  { id: 'maya', label: 'Maya', icon: 'fa-wallet', tone: 'text-success' },
  { id: 'card', label: 'Credit Card', icon: 'fa-credit-card', tone: 'text-warning' },
  { id: 'grabpay', label: 'GrabPay', icon: 'fa-car', tone: 'text-danger' },
];
const peso = (v) => `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PHONE_RE = /^\+?[0-9][0-9 ()-]{6,22}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const loadStored = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
  } catch {
    return null;
  }
};

export default function GuestCheckoutSessionPage() {
  const routeParams = useParams();
  const [query] = useSearchParams();
  // The charger QR carries chargerId / connector / signature / nonce either in the path (/c/:chargerId/:connector) or the query.
  const qr = {
    chargerId: routeParams.chargerId || query.get('chargerId') || '',
    connectorIndex: routeParams.connectorIndex || query.get('connector') || '',
    s: query.get('s') || '',
    n: query.get('n') || '',
  };
  const hasQr = Boolean(qr.chargerId && qr.connectorIndex && qr.s && qr.n);

  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [method, setMethod] = useState('gcash');
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [session, setSession] = useState(null);
  const token = useRef(null);
  const config = usePublicConfig();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  // A scanned sticker is a link to this page for one connector: check its shape, then open it.
  const onScanned = (text) => {
    if (chargerQrProblem(text)) { setScanError('That is not a ChargeOS charger code. Scan the sticker next to the connector.'); return; }
    const url = new URL(text.trim());
    setScanning(false);
    setScanError('');
    navigate(`${url.pathname}${url.search}`);
  };
  const pollRef = useRef(null);

  // Resume a session that is already in progress in this browser (e.g. page refresh while charging).
  useEffect(() => {
    const stored = loadStored();
    const gsParam = query.get('gs');
    if (stored && (!gsParam || gsParam === stored.id)) {
      token.current = stored.token;
      api
        .getGuestSession(stored.id, stored.token)
        .then(setSession)
        // Forget the saved session only when the server says it is gone; a network blip or a 5xx must not
        // throw away the one token that lets this guest stop charging and see the receipt.
        .catch((err) => { if ([401, 404].includes(err?.status)) localStorage.removeItem(STORE_KEY); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasQr) return;
    api
      .getGuestConnector(qr)
      .then(setInfo)
      .catch((e) => setError(e.message || 'Could not verify this charger QR code.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qr.chargerId, qr.connectorIndex, qr.s, qr.n, hasQr]);

  const refresh = useCallback(async () => {
    if (!session || !token.current) return;
    try {
      setSession(await api.getGuestSession(session.id, token.current));
    } catch (e) {
      setError(e.message);
    }
  }, [session]);

  // Poll while payment is being confirmed or energy is being delivered.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (session && ['pending_payment', 'starting', 'charging', 'stopping'].includes(session.status)) {
      pollRef.current = setInterval(refresh, session.status === 'charging' ? 4000 : 2000);
    }
    return () => clearInterval(pollRef.current);
  }, [session, refresh]);

  const validate = () => {
    const e = {};
    if (!EMAIL_RE.test(email.trim())) e.email = 'A valid email is required to deliver your payment receipt.';
    if (phone.trim() && !PHONE_RE.test(phone.trim())) e.phone = 'Enter a valid mobile number.';
    if (!terms) e.terms = 'Please accept the tariff terms to continue.';
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setError('');
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      const out = await api.createGuestSession({ qr, email: email.trim(), phone: phone.trim() || undefined, paymentMethod: method, acceptTerms: true });
      token.current = out.accessToken;
      localStorage.setItem(STORE_KEY, JSON.stringify({ id: out.session.id, token: out.accessToken }));
      setSession(out.session);
      if (!out.session.mock) window.location.assign(out.session.checkoutUrl);
    } catch (err) {
      setError(err.message || 'Could not start the payment.');
    } finally {
      setSubmitting(false);
    }
  };

  const authorize = async () => {
    setSubmitting(true);
    setError('');
    try {
      setSession(await api.simulateGuestPayment(session.id, token.current));
    } catch (err) {
      setError(err.message || 'Payment authorization failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const stop = async () => {
    setStopping(true);
    setError('');
    try {
      setSession(await api.stopGuestSession(session.id, token.current));
      localStorage.removeItem(STORE_KEY);
    } catch (err) {
      setError(err.message || 'Could not stop the session.');
      await refresh();
    } finally {
      setStopping(false);
    }
  };

  const startOver = () => {
    localStorage.removeItem(STORE_KEY);
    setSession(null);
  };

  const status = session?.status;
  const showForm = !session || ['rejected', 'expired'].includes(status);
  const awaitingCheckout = session && status === 'pending_payment';
  const active = session && ['starting', 'charging', 'stopping'].includes(status);
  const done = session && status === 'completed';
  const held = session && status === 'audit_hold';
  const holdAmount = info?.holdAmount ?? config.guestHoldAmount ?? 500;
  const station = info;
  const tariff = info?.tariff;

  return (
    <div className="container-fluid py-4" style={{ marginTop: 56 }}>
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-8 col-xl-7">
          <div className="text-center mb-4">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3" style={{ width: 56, height: 56, background: '#e0f2fe', border: '2px solid #bae6fd' }}>
              <i className="fa fa-qrcode fa-2x" style={BLUE} aria-hidden="true" />
            </div>
            <h1 className="h3 fw-bold mb-1" style={BLUE}>Ad-Hoc Guest Charging Portal</h1>
            <p className="text-muted small mb-0">Charge your EV immediately without registration. Fast, secure checkout powered by PayMongo.</p>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert" id="guest-error">
              {error}
            </div>
          )}
          {session && ['rejected', 'expired'].includes(status) && (
            <div className="alert alert-warning" role="alert" id="guest-closed">
              This session ended without charging ({session.stopReason || status}). Any payment hold has been released.
            </div>
          )}

          {showForm && (
            <div id="guest-form-card" className="card p-4 p-md-5 mb-4 shadow-sm border bg-white">
              {!hasQr && (
                <div className="alert alert-info" id="guest-no-qr">
                  <p className="mb-2"><strong>Scan the QR code on the charger to begin.</strong> Each connector has its own sticker; scanning it shows the price and lets you pay without an account.</p>
                  <button type="button" className="btn btn-primary btn-lg w-100 fw-bold" id="btn-guest-scan" onClick={() => { setScanError(''); setScanning(true); }}>
                    <i className="fa fa-qrcode me-2" aria-hidden="true" />Scan charger QR code
                  </button>
                </div>
              )}
              {station && (
                <div className="p-3 rounded mb-4 bg-light border" id="guest-station">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <span className="badge text-bg-primary small mb-1">QR Code Scanned</span>
                      <h5 className="fw-bold mb-0" style={BLUE}>
                        {station.siteName} &bull; Connector {station.connectorIndex}
                      </h5>
                      <small className="text-muted">
                        {station.vendor} {station.model} &bull; {station.connectorType} {station.maxPowerKw} kW ({station.chargerId})
                      </small>
                    </div>
                    <span className={`badge ${station.available ? 'text-bg-success' : 'text-bg-secondary'}`}>{station.plugInserted ? 'Plug Inserted' : station.available ? 'Available' : station.status}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between small text-muted flex-wrap gap-2">
                    <span>Tariff Rate: <strong className="text-dark">{peso(tariff.energyRateKwh)} / kWh</strong></span>
                    <span>Session Fee: <strong className="text-dark">{peso(tariff.sessionFee)}</strong></span>
                    <span>Idle Fee: <strong className="text-dark">{peso(tariff.idleFeeMin)} / min</strong></span>
                  </div>
                </div>
              )}

              <form id="guest-checkout-form" onSubmit={submit} noValidate>
                <div className="mb-3">
                  <label htmlFor="guest-email" className="form-label small fw-bold">Email for Digital Receipt (CDR)</label>
                  <div className="input-group has-validation">
                    <span className="input-group-text bg-light"><i className="fa fa-envelope text-muted" aria-hidden="true" /></span>
                    <input type="email" className={`form-control${errors.email ? ' is-invalid' : ''}`} id="guest-email" placeholder="driver@gmail.com" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={Boolean(errors.email)} />
                    <div className="invalid-feedback" id="guest-email-error">{errors.email}</div>
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="guest-phone" className="form-label small fw-bold">Mobile Number for SMS Status Notifications</label>
                  <div className="input-group has-validation">
                    <span className="input-group-text bg-light"><i className="fa fa-phone text-muted" aria-hidden="true" /></span>
                    <input type="tel" className={`form-control${errors.phone ? ' is-invalid' : ''}`} id="guest-phone" placeholder="+63 917 123 4567" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} aria-invalid={Boolean(errors.phone)} />
                    <div className="invalid-feedback" id="guest-phone-error">{errors.phone}</div>
                  </div>
                </div>

                <div className="card p-3 mb-4 border" style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-bold small" style={{ color: '#92400e' }}><i className="fa fa-lock me-1" />Security Pre-Authorization Hold:</span>
                    <span className="h5 fw-bold mb-0" style={{ color: '#d97706' }} id="guest-hold-amount">{peso(holdAmount)} PHP</span>
                  </div>
                  <p className="small mb-0" style={{ color: '#78350f' }}>
                    PayMongo will place a temporary hold of {peso(holdAmount)} on your payment method. When you stop charging, only the amount consumed is charged and the rest of the hold is released (e-wallets refund the difference, which can take a little while to show).
                  </p>
                </div>

                <div className="form-label small fw-bold mb-2">Select PayMongo Payment Method</div>
                <div className="row mb-4" role="radiogroup" aria-label="PayMongo payment method">
                  {METHODS.map((m) => (
                    <div className="col-6 col-sm-3 mb-2" key={m.id}>
                      <div
                        className={`pay-method-card p-2 text-center rounded${method === m.id ? ' active' : ''}`}
                        data-method={m.id}
                        role="radio"
                        aria-checked={method === m.id}
                        tabIndex={0}
                        onClick={() => setMethod(m.id)}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setMethod(m.id)}
                      >
                        <i className={`fa ${m.icon} ${m.tone} fa-lg mb-1 d-block`} aria-hidden="true" />
                        <small className="fw-bold text-dark d-block">{m.label}</small>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mb-4">
                  <div className="form-check">
                    <input type="checkbox" className={`form-check-input${errors.terms ? ' is-invalid' : ''}`} id="guest-terms" checked={terms} onChange={(e) => setTerms(e.target.checked)} required />
                    <label className="form-check-label text-muted small" htmlFor="guest-terms">
                      I accept the charging tariff rates, idle fee policy{tariff?.gracePeriod != null ? ` (after ${tariff.gracePeriod} min grace)` : ''}, and terms of service.
                    </label>
                    {errors.terms && <div className="invalid-feedback d-block" id="guest-terms-error">{errors.terms}</div>}
                  </div>
                </div>

                <button type="submit" id="btn-guest-pay" className="btn btn-primary w-100 fw-bold py-3 mb-3" disabled={submitting || !hasQr || (info && !info.available)}>
                  {submitting && <span id="guest-spinner" className="spinner-border spinner-border-sm me-2" role="status" />}
                  <span id="guest-btn-text">
                    {submitting ? 'Processing PayMongo Hold...' : (<><i className="fa fa-bolt me-2" aria-hidden="true" />Authorize {peso(holdAmount)} &amp; Start Charging</>)}
                  </span>
                </button>
              </form>

              <div className="text-center">
                <Link to="/register" className="text-muted small">
                  Charge often? <span className="fw-bold" style={BLUE}>Register an account</span> for a prepaid wallet and reservations
                </Link>
              </div>
            </div>
          )}

          {awaitingCheckout && session.mock && (
            <div id="guest-mock-checkout" className="card p-4 mb-4 shadow-sm border bg-white text-center">
              <h4 className="fw-bold" style={BLUE}>PayMongo Checkout (Sandbox)</h4>
              <p className="text-muted small mb-1">Paying with {session.paymentMethodLabel}</p>
              <p className="h4 fw-bold mb-3" style={{ color: '#d97706' }}>{peso(session.holdAmount)} pre-authorization hold</p>
              <button type="button" id="btn-guest-authorize" className="btn btn-success fw-bold py-2 px-4" onClick={authorize} disabled={submitting}>
                {submitting && <span className="spinner-border spinner-border-sm me-2" role="status" />}
                Authorize payment
              </button>
            </div>
          )}
          {awaitingCheckout && !session.mock && (
            <div className="alert alert-info" id="guest-awaiting">Waiting for PayMongo to confirm your payment...</div>
          )}

          {active && (
            <div id="guest-active-card" className="card p-4 p-md-5 mb-4 shadow-sm text-center bg-white" style={{ border: '2px solid #16a34a', borderRadius: 16 }}>
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 mx-auto" style={{ width: 60, height: 60, background: '#ecfdf5', border: '2px solid #16a34a' }}>
                <i className="fa fa-bolt text-success fa-2x" aria-hidden="true" />
              </div>
              <h3 className="fw-bold mb-1" style={BLUE}>Charging in Progress</h3>
              <p className="text-muted small mb-3">Session Ref: <strong className="text-primary" id="guest-sess-code">{session.sessionCode}</strong></p>
              <div className="row mb-4">
                <div className="col-6 mb-2">
                  <div className="p-3 rounded bg-light border">
                    <span className="text-muted small d-block">Energy Delivered</span>
                    <span className="h4 fw-bold text-dark mb-0" id="guest-kwh">{session.telemetry.energyKwh.toFixed(2)} kWh</span>
                  </div>
                </div>
                <div className="col-6 mb-2">
                  <div className="p-3 rounded bg-light border">
                    <span className="text-muted small d-block">Current Accrued Cost</span>
                    <span className="h4 fw-bold mb-0" style={{ color: '#d97706' }} id="guest-cost">{peso(session.telemetry.accruedCost)}</span>
                  </div>
                </div>
              </div>
              <button type="button" id="btn-guest-stop" className="btn btn-danger fw-bold py-3 px-5 mb-2" onClick={stop} disabled={stopping || status !== 'charging'}>
                {stopping ? (<><span className="spinner-border spinner-border-sm me-2" />Settling PayMongo Transaction...</>) : (<><i className="fa fa-stop-circle me-2" aria-hidden="true" />Stop Session &amp; Settle Payment</>)}
              </button>
            </div>
          )}

          {held && (
            <div className="alert alert-warning" id="guest-audit-hold">
              Your session has stopped but payment settlement needs a manual review. Your hold has not been lost; our team will contact you.
            </div>
          )}

          {done && session.receipt && (
            <div id="guest-receipt-card" className="card p-4 p-md-5 mb-4 shadow-sm border bg-white" style={{ borderRadius: 16 }}>
              <div className="text-center mb-4">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3" style={{ width: 56, height: 56, background: '#ecfdf5', border: '2px solid #16a34a' }}>
                  <i className="fa fa-check text-success fa-2x" aria-hidden="true" />
                </div>
                <h3 className="fw-bold mb-1" style={BLUE}>Charging Session Complete</h3>
                <p className="text-muted small mb-0">E-Receipt &bull; Ref: <strong className="text-dark" id="receipt-cdr">{session.receipt.cdrNumber}</strong></p>
              </div>
              <div className="table-responsive mb-4">
                <table className="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr><td className="text-muted">Delivered Energy ({session.receipt.energyKwh.toFixed(2)} kWh)</td><td className="text-end text-dark fw-bold" id="receipt-energy">{peso(session.receipt.energyCost)}</td></tr>
                    <tr><td className="text-muted">Session Initiation Fee</td><td className="text-end text-dark fw-bold">{peso(session.receipt.sessionFee)}</td></tr>
                    {session.receipt.parkingCost > 0 && <tr><td className="text-muted">Parking</td><td className="text-end text-dark fw-bold">{peso(session.receipt.parkingCost)}</td></tr>}
                    <tr><td className="text-muted">VAT (12% Included)</td><td className="text-end text-muted small">{peso(session.receipt.vat)}</td></tr>
                    <tr className="border-top fw-bold">
                      <td className="text-dark">Total Billed Amount</td>
                      <td className="text-end text-success fw-bold" id="receipt-total">{peso(session.receipt.total)} PHP</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="alert alert-success p-3 small mb-4" style={{ backgroundColor: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46' }}>
                <div className="d-flex justify-content-between mb-1"><span>PayMongo Pre-Auth Hold:</span><strong id="receipt-hold">{peso(session.receipt.holdAmount)}</strong></div>
                <div className="d-flex justify-content-between mb-1"><span>Captured Settlement:</span><strong id="receipt-captured">{peso(session.receipt.captured)}</strong></div>
                <hr className="my-1 border-success" />
                <div className="d-flex justify-content-between fw-bold"><span>Released Back to Card/Wallet:</span><span id="receipt-released">{peso(session.receipt.released)}</span></div>
              </div>
              {session.receipt.deliveries?.length > 0 && (
                <p className="small text-muted text-center" id="receipt-delivery">
                  Receipt {session.receipt.deliveries.map((d) => `${d.status === 'sent' ? 'sent by' : 'queued for'} ${d.channel === 'sms' ? 'SMS' : 'email'}`).join(' and ')}.
                </p>
              )}
              <Link to="/station-locator-availability" className="btn btn-outline-primary w-100 fw-bold py-2" onClick={startOver}>
                <i className="fa fa-home me-2" aria-hidden="true" />Back to Station Locator
              </Link>
            </div>
          )}
        </div>
      </div>
      {scanning && <QrScanner onResult={onScanned} onClose={() => setScanning(false)} error={scanError} />}
    </div>
  );
}
